import React, { useState, useEffect, useMemo } from 'react';
import { 
  CheckCircle, 
  FileText, 
  DollarSign,
  Users,
  Shield,
  Calendar,
  AlertTriangle,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import type { QuoteWizardData } from '../QuoteWizard';
import type { WizardStepProps } from '../../../framework/wizard';
import { quoteService } from '../../../services/quoteService';
import { farmerService } from '../../../services/farmerService';
import { organizationService } from '../../../services/organizationService';
import { toast } from '@etherisc/ui-kit';
import { getPricingBreakdown, formatCurrency as formatPricingCurrency } from '../utils/pricingCalculator';
import type { QuotationAggregates } from '../../../types/quote';
import { usePermissions } from '../../../hooks/usePermissions';

export const SummaryApprovalStepNew: React.FC<WizardStepProps<QuoteWizardData>> = ({ 
  data, 
  updateData, 
  isValid, 
  errors 
}) => {
  const { hasPermission } = usePermissions();
  const [isApproving, setIsApproving] = useState(false);
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  const [participantDetails, setParticipantDetails] = useState<Record<string, { name: string; location: string }>>({});
  const [loadingParticipants, setLoadingParticipants] = useState(true);
  const [aggregates, setAggregates] = useState<QuotationAggregates | null>(null);
  const [loadingAggregates, setLoadingAggregates] = useState(true);

  // Load participant details (farmers and organizations)
  useEffect(() => {
    const loadParticipantDetails = async () => {
      if (!data.quote?.participants?.length) {
        setLoadingParticipants(false);
        return;
      }

      try {
        setLoadingParticipants(true);
        const details: Record<string, { name: string; location: string }> = {};

        // Load details for each participant
        for (const participant of data.quote.participants) {
          if (participant.farmerId) {
            try {
              const farmerResponse = await farmerService.getFarmerById(participant.farmerId);
              const farmer = farmerResponse.farmer;
              
              // Build location string with district and province
              let location = 'No location';
              if (farmer.administrativeLevels) {
                const levels = farmer.administrativeLevels;
                const locationParts = [];
                
                // Add administrative levels in order: District (level 2), Province (level 1)
                // Also include Ward (level 3) and Village (level 4) if available
                if (levels['4']) locationParts.push(levels['4']); // Village
                if (levels['3']) locationParts.push(levels['3']); // Ward  
                if (levels['2']) locationParts.push(levels['2']); // District
                if (levels['1']) locationParts.push(levels['1']); // Province
                
                if (locationParts.length > 0) {
                  location = locationParts.join(', ');
                }
              } else {
                // Fallback to legacy fields
                const locationParts = [];
                if (farmer.ward) locationParts.push(farmer.ward);
                if (farmer.county) locationParts.push(farmer.county);
                if (farmer.address) locationParts.push(farmer.address);
                
                if (locationParts.length > 0) {
                  location = locationParts.join(', ');
                } else {
                  location = 'No location';
                }
              }
              
              details[participant.farmerId] = {
                name: farmer.name || 'Unknown Farmer',
                location
              };
            } catch (error) {
              console.error(`Error loading farmer ${participant.farmerId}:`, error);
              details[participant.farmerId] = {
                name: 'Unknown Farmer',
                location: 'No location'
              };
            }
          } else if (participant.organizationId) {
            try {
              const organization = await organizationService.getOrganizationById(participant.organizationId);
              
              // Build location string from organization fields (limited data available)
              let location = 'No location';
              if (organization.name) {
                location = `${organization.name} (${organization.type})`;
              }
              
              details[participant.organizationId] = {
                name: organization.name || 'Unknown Organization',
                location
              };
            } catch (error) {
              console.error(`Error loading organization ${participant.organizationId}:`, error);
              details[participant.organizationId] = {
                name: 'Unknown Organization',
                location: 'No location'
              };
            }
          }
        }

        setParticipantDetails(details);
      } catch (error) {
        console.error('Error loading participant details:', error);
      } finally {
        setLoadingParticipants(false);
      }
    };

    loadParticipantDetails();
  }, [data.quote?.participants]);

  // Load quotation aggregates from backend
  useEffect(() => {
    const loadAggregates = async () => {
      if (!data.quote?.id) {
        setLoadingAggregates(false);
        return;
      }

      try {
        setLoadingAggregates(true);
        const response = await quoteService.getQuotationSummary(data.quote.id);
        
        // The API client returns the data directly (unwrapped)
        const aggregatesData = response?.quotationResults?.aggregates;
        
        if (aggregatesData) {
          setAggregates(aggregatesData);
        } else {
          setAggregates(null);
        }
      } catch {
        // Endpoint might not be available yet - graceful fallback
        setAggregates(null);
      } finally {
        setLoadingAggregates(false);
      }
    };

    loadAggregates();
  }, [data.quote?.id, data.quote?.protectionLevels]);

  const handleApproval = async () => {
    if (!data.quote?.id) {
      toast.error('Quote ID not found');
      return;
    }

    try {
      setIsApproving(true);
      
      // Use state machine-based approval
      const response = await fetch(`/api/quotes/${data.quote.id}/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Quote approval error response:', errorData);
        const errorBody = errorData.error ?? errorData;
        const validationErrors = errorBody.details?.validationErrors;
        let errorMessage = errorBody.message || 'Failed to approve quote';
        
        if (validationErrors && Array.isArray(validationErrors) && validationErrors.length > 0) {
          const formattedErrors = validationErrors.map((err: { field: string; message: string }) => 
            `• ${err.message}`
          ).join('\n');
          errorMessage = `Validation failed:\n${formattedErrors}`;
        }
        
        throw new Error(errorMessage);
      }

      const result = await response.json();
      
      // Update local data with the returned quote
      updateData({
        quote: {
          ...data.quote,
          ...result.data.quote,
          state: 'approved'
        }
      });

      toast.success('Quote approved successfully! Payment can be initiated when ready.');
      setShowApprovalDialog(false);
    } catch (error) {
      console.error('Error approving quote:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to approve quote. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsApproving(false);
    }
  };

  const formatCurrency = (amount: number | string | undefined) => {
    if (amount === undefined || amount === null) return 'N/A';
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(num)) return 'N/A';
    return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (date: Date | string | undefined) => {
    if (!date) return 'N/A';
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString();
  };

  const quote = data.quote;
  const product = quote?.product;
  const participants = quote?.participants || [];

  const PAGE_SIZE = 50;
  const [currentPage, setCurrentPage] = useState(1);

  const policyholderParticipant = useMemo(
    () => participants.find(p => p.role === 'POLICYHOLDER' && p.organizationId),
    [participants]
  );
  const insuredParticipants = useMemo(
    () => quote?.isMasterPolicy
      ? participants.filter(p => !(p.role === 'POLICYHOLDER' && p.organizationId))
      : participants,
    [participants, quote?.isMasterPolicy]
  );
  const totalInsuredPages = Math.max(1, Math.ceil(insuredParticipants.length / PAGE_SIZE));
  const paginatedParticipants = useMemo(
    () => insuredParticipants.length > PAGE_SIZE
      ? insuredParticipants.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)
      : insuredParticipants,
    [insuredParticipants, currentPage]
  );


  // Use backend aggregates for pricing (preferred) or fallback to frontend calculation
  const costDetails = getPricingBreakdown(undefined, data, aggregates || undefined);
  
  const isApproved = quote?.state === 'approved';


  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-blue-600" />
          <h4 className="text-lg font-medium text-gray-900">Quote Summary & Approval</h4>
        </div>
        {isApproved && (
          <div className="flex items-center gap-2 text-green-600">
            <CheckCircle className="h-5 w-5" />
            <span className="text-sm font-medium">Approved</span>
          </div>
        )}
      </div>

      {/* Product Information */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h5 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
          <Shield className="h-5 w-5 text-blue-600" />
          Product Information
        </h5>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-gray-500">Product Name</label>
            <p className="text-sm text-gray-900">{product?.name || 'N/A'}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">Product Code</label>
            <p className="text-sm text-gray-900">{product?.code || 'N/A'}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">Version</label>
            <p className="text-sm text-gray-900">{product?.version || 'N/A'}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">Status</label>
            <p className="text-sm text-gray-900">{product?.status || 'N/A'}</p>
          </div>
          {product?.tags && product.tags.length > 0 && (
            <div className="md:col-span-2">
              <label className="text-sm font-medium text-gray-500">Product Tags</label>
              <div className="flex flex-wrap gap-2 mt-1">
                {product.tags.map((tag, index) => (
                  <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Policy Details */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h5 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
          <FileText className="h-5 w-5 text-blue-600" />
          Policy Details
        </h5>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-gray-500">Policy Type</label>
            <p className="text-sm text-gray-900">
              {quote?.isMasterPolicy ? 'Master Policy' : 'Individual Policy'}
            </p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">Coverage Start Date</label>
            <p className="text-sm text-gray-900 flex items-center gap-1">
              <Calendar className="h-4 w-4 text-gray-400" />
              {formatDate(quote?.coverageStartDate)}
            </p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">Coverage End Date</label>
            <p className="text-sm text-gray-900 flex items-center gap-1">
              <Calendar className="h-4 w-4 text-gray-400" />
              {formatDate(quote?.coverageEndDate)}
            </p>
          </div>
          
          {/* Underlying Activities */}
          {quote?.underlying_assets?.activities_products && quote.underlying_assets.activities_products.length > 0 && (
            <div className="md:col-span-2">
              <label className="text-sm font-medium text-gray-500">Underlying Activities/Products</label>
              <div className="mt-1">
                {quote.underlying_assets.activities_products.map((activity, index) => (
                  <div key={index} className="text-sm text-gray-900 py-1">
                    • {activity.type} {activity.area_hectares && `(${activity.area_hectares} ha)`}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Selected Perils */}
          {quote?.perils_config?.perils && quote.perils_config.perils.length > 0 && (
            <div className="md:col-span-2">
              <label className="text-sm font-medium text-gray-500">Selected Perils</label>
              <div className="mt-1">
                {quote.perils_config.perils.map((peril, index) => (
                  <div key={index} className="text-sm text-gray-900 py-1 flex justify-between">
                    <span>• {peril.peril_id}</span>
                    {peril.weight && <span className="text-gray-500">{peril.weight}%</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Cost Details - Pricing Summary */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h5 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
          <DollarSign className="h-5 w-5 text-blue-600" />
          Pricing Summary
        </h5>
        {loadingAggregates ? (
          <div className="text-center py-4">
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
              Loading pricing calculation...
            </div>
          </div>
        ) : costDetails.total > 0 ? (
          <div className="space-y-4">
            {/* Two-column layout: Financial values */}
            <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">ANRP (Base Premium):</span>
                <span className="font-medium">{formatPricingCurrency(costDetails.anrp)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Loadings:</span>
                <span className="font-medium">{formatPricingCurrency(costDetails.loadings)}</span>
              </div>

              <div className="flex justify-between">
                <span className="text-gray-600">GRP (Gross Premium):</span>
                <span className="font-medium">{formatPricingCurrency(costDetails.grp)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Tax Amount:</span>
                <span className="font-medium">{formatPricingCurrency(costDetails.tax)}</span>
              </div>

              <div></div> {/* Empty cell for spacing */}
              <div className="flex justify-between">
                <span className="text-gray-600">Subsidy Amount:</span>
                <span className="font-medium text-green-600">-{formatPricingCurrency(costDetails.subsidy)}</span>
              </div>
            </div>
            
            {/* Total Payable */}
            <div className="border-t border-green-300 pt-4 flex justify-end">
              <div className="text-right">
                <div className="text-lg font-bold text-green-800">
                  Total Payable: {formatPricingCurrency(costDetails.total)}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-4 text-gray-500">
            No pricing calculation available
          </div>
        )}
      </div>

      {/* Policyholder (separate section for master policies) */}
      {quote?.isMasterPolicy && policyholderParticipant && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h5 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
            <Shield className="h-5 w-5 text-blue-600" />
            Policyholder
          </h5>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-500">Organization</label>
              <p className="text-sm text-gray-900 font-medium">
                {participantDetails[policyholderParticipant.organizationId || '']?.name || 'Loading...'}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Role</label>
              <p className="text-sm text-gray-900">Policyholder</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Type</label>
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                Organization
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Insured Participants */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h5 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
          <Users className="h-5 w-5 text-blue-600" />
          {quote?.isMasterPolicy ? `Insureds (${insuredParticipants.length})` : 'Policyholder Details'}
        </h5>
        
        {insuredParticipants.length === 0 ? (
          <p className="text-sm text-gray-500">No participants configured</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sum Insured</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hectares/Animals</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Premium</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loadingParticipants ? (
                    <tr>
                      <td colSpan={5} className="px-6 py-4 text-center">
                        <div className="flex items-center justify-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                          Loading participant details...
                        </div>
                      </td>
                    </tr>
                  ) : (
                    paginatedParticipants.map((participant, index) => {
                      const participantId = participant.farmerId || participant.organizationId;
                      const details = participantId ? participantDetails[participantId] : null;
                      
                      return (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900 font-medium">{details?.name || 'Loading...'}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm text-gray-900">
                              {participant.role === 'POLICYHOLDER' ? 'Policyholder' : 'Insured'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm font-medium text-gray-900">
                              {participant.sumInsured ? formatCurrency(participant.sumInsured) : 'N/A'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm font-medium text-gray-900">
                              {participant.hectaresAnimalCount ? participant.hectaresAnimalCount.toLocaleString() : 'N/A'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm font-medium text-gray-900">
                              {(() => {
                                if (!participant.farmerId || !data.quote) return 'N/A';
                                
                                if (aggregates) {
                                  const farmerAggregate = aggregates.participants.find(p => p.farmerId === participant.farmerId);
                                  if (farmerAggregate && farmerAggregate.total_payable > 0) {
                                    return formatPricingCurrency(farmerAggregate.total_payable);
                                  }
                                  if (aggregates.total && aggregates.total.total_payable > 0 && participant.sumInsured && data.quote.sumInsured) {
                                    const premiumCents = Math.round(
                                      (Number(participant.sumInsured) / Number(data.quote.sumInsured)) * aggregates.total.total_payable * 100
                                    );
                                    return formatPricingCurrency(premiumCents / 100);
                                  }
                                }
                                
                                if (participant.sumInsured && data.quote.sumInsured && data.quote.premiumAmount) {
                                  const premiumCents = Math.round(
                                    (Number(participant.sumInsured) / Number(data.quote.sumInsured)) * Number(data.quote.premiumAmount) * 100
                                  );
                                  return formatPricingCurrency(premiumCents / 100);
                                }
                                
                                return 'N/A';
                              })()}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination controls */}
            {insuredParticipants.length > PAGE_SIZE && (
              <div className="flex items-center justify-between border-t border-gray-200 pt-4 mt-4">
                <p className="text-sm text-gray-700">
                  Showing {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, insuredParticipants.length)} of {insuredParticipants.length} insureds
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Previous
                  </button>
                  <span className="text-sm text-gray-700">
                    Page {currentPage} of {totalInsuredPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalInsuredPages, p + 1))}
                    disabled={currentPage === totalInsuredPages}
                    className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Approval Section */}
      {!isApproved && hasPermission('quotes:approve') && (
        <div className="bg-yellow-50 border border-gray-200 rounded-xl p-8 shadow-2xl mt-8 relative z-10">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
          <div className="flex-1">
            <h5 className="text-lg font-medium text-yellow-800">Ready for Approval</h5>
            <p className="text-sm text-yellow-700 mt-1">
              Please review all details above carefully. Once approved, this quote will be finalized and ready for policy issuance.
            </p>
            <div className="mt-4">
              <button
                onClick={() => setShowApprovalDialog(true)}
                disabled={isApproving || isApproved}
                style={{
                  backgroundColor: '#16a34a', // green-600
                  color: 'white'
                }}
                className="inline-flex items-center px-8 py-4 text-lg font-bold rounded-lg hover:bg-green-700 focus:outline-none focus:ring-4 focus:ring-green-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-xl transform hover:scale-105 transition-all duration-200 relative z-20"
              >
                {isApproving ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Approving...
                  </>
                ) : isApproved ? (
                  <>
                    <CheckCircle className="h-5 w-5 mr-2" />
                    Already Approved
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-5 w-5 mr-2" />
                    Approve Quote
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Approval Confirmation Dialog */}
      {showApprovalDialog && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <div className="mt-3 text-center">
                <h3 className="text-lg leading-6 font-medium text-gray-900">Approve Quote</h3>
                <div className="mt-2 px-7 py-3">
                  <p className="text-sm text-gray-500">
                    Are you sure you want to approve this quote? This action cannot be undone and will finalize the quote for policy issuance.
                  </p>
                </div>
                <div className="flex gap-3 px-4 py-3">
                  <button
                    onClick={() => setShowApprovalDialog(false)}
                    className="flex-1 px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-md text-sm font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleApproval}
                    disabled={isApproving}
                    style={{
                      backgroundColor: '#16a34a', // green-600 (same as main button)
                      color: 'white'
                    }}
                    className="flex-1 px-4 py-2 border border-transparent rounded-md text-sm font-medium hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                  >
                    {isApproving ? 'Approving...' : 'Approve'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Validation Errors */}
      {!isValid && errors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Please resolve the following issues:</h3>
              <ul className="mt-2 text-sm text-red-700 list-disc list-inside">
                {errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
